export type AdminCouponItem = {
  id: number;
  code: string;
  description: string;
  discount: number;
  maxDiscount: number;
  enabled: boolean;
  type: "PERCENTAGE" | "FIXED";
  expiryDate: string | null;
  expiryDateInput: string;
  addedOn: string | null;
  usageCount: number;
  status: "Active" | "Expired" | "Disabled";
};

export type AdminCouponsResponse = {
  coupons: AdminCouponItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalCoupons: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  summary: {
    totalCoupons: number;
    activeCoupons: number;
    expiredCoupons: number;
    usedCoupons: number;
  };
};

export type AdminCouponInput = {
  code: string;
  description?: string;
  discount: number;
  maxDiscount: number;
  enabled?: boolean;
  type: "PERCENTAGE" | "FIXED";
  expiryDate?: string;
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchAdminCoupons(query: URLSearchParams) {
  const response = await fetch(`/api/admin/coupons?${query.toString()}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminCouponsResponse;
}

export async function fetchAdminCoupon(couponId: number) {
  const response = await fetch(`/api/admin/coupons/${couponId}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as AdminCouponItem;
}

export async function createAdminCoupon(input: AdminCouponInput) {
  const response = await fetch("/api/admin/coupons", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function updateAdminCoupon(couponId: number, input: AdminCouponInput & { enabled: boolean }) {
  const response = await fetch(`/api/admin/coupons/${couponId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}
